import { apiFailureSchema, type ApiErrorCode, type FieldErrors } from "@tm/contracts";
import { z } from "zod";

/** Error codes produced by the client itself when no API response could be used. */
export type ClientErrorCode = ApiErrorCode | "NETWORK_ERROR" | "INVALID_RESPONSE";

export class ApiError extends Error {
  readonly code: ClientErrorCode;
  readonly status: number;
  readonly fieldErrors: FieldErrors;

  constructor(
    code: ClientErrorCode,
    message: string,
    status: number,
    fieldErrors: FieldErrors = {},
  ) {
    super(message);
    this.name = "ApiError";
    this.code = code;
    this.status = status;
    this.fieldErrors = fieldErrors;
  }
}

export function isApiError(error: unknown, code?: ClientErrorCode): error is ApiError {
  return error instanceof ApiError && (code === undefined || error.code === code);
}

/** Human-readable message for any thrown value. */
export function getErrorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  return "Something went wrong. Please try again.";
}

type QueryValue = string | number | boolean | undefined;
type HttpMethod = "GET" | "POST" | "PATCH" | "PUT" | "DELETE";

interface RequestOptions<S extends z.ZodType> {
  schema: S;
  method?: HttpMethod;
  body?: unknown;
  query?: Readonly<Record<string, QueryValue>>;
  signal?: AbortSignal | undefined;
}

const API_BASE = (import.meta.env.VITE_API_URL ?? "/api").replace(/\/+$/, "");
const successEnvelopeSchema = z.object({ success: z.literal(true), data: z.unknown() });

let unauthorizedHandler: (() => void) | null = null;

/** Registers a callback invoked whenever the API answers 401 (session missing or expired). */
export function onUnauthorized(handler: () => void): () => void {
  unauthorizedHandler = handler;
  return () => {
    if (unauthorizedHandler === handler) unauthorizedHandler = null;
  };
}

function buildUrl(path: string, query: RequestOptions<z.ZodType>["query"]): string {
  const url = `${API_BASE}${path}`;
  if (!query) return url;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== "") params.set(key, String(value));
  }
  const search = params.toString();
  return search ? `${url}?${search}` : url;
}

/**
 * The only function in the web app that calls `fetch`. It sends cookies, serializes JSON,
 * unwraps the `{ success, data }` envelope, validates `data` against the contract schema,
 * and converts failures into typed `ApiError`s.
 */
export async function apiRequest<S extends z.ZodType>(
  path: string,
  options: RequestOptions<S>,
): Promise<z.output<S>> {
  const { schema, method = "GET", body, query, signal } = options;

  let response: Response;
  try {
    response = await fetch(buildUrl(path, query), {
      method,
      credentials: "include",
      headers: {
        Accept: "application/json",
        ...(body !== undefined && { "Content-Type": "application/json" }),
      },
      ...(body !== undefined && { body: JSON.stringify(body) }),
      ...(signal && { signal }),
    });
  } catch (error: unknown) {
    if (error instanceof DOMException && error.name === "AbortError") throw error;
    throw new ApiError("NETWORK_ERROR", "Unable to reach the server. Check your connection.", 0);
  }

  const payload: unknown = await response.json().catch(() => null);

  if (!response.ok) {
    if (response.status === 401) unauthorizedHandler?.();
    const failure = apiFailureSchema.safeParse(payload);
    if (failure.success) {
      const { code, message, details } = failure.data.error;
      throw new ApiError(code, message, response.status, details?.fieldErrors ?? {});
    }
    throw new ApiError(
      response.status === 429 ? "RATE_LIMITED" : "INTERNAL_ERROR",
      "Something went wrong. Please try again.",
      response.status,
    );
  }

  const envelope = successEnvelopeSchema.safeParse(payload);
  const data = envelope.success ? schema.safeParse(envelope.data.data) : null;
  if (!data?.success) {
    throw new ApiError(
      "INVALID_RESPONSE",
      "The server returned an unexpected response.",
      response.status,
    );
  }
  return data.data;
}
